import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  CheckCircle, CheckCircle2, Target, Phone, RefreshCw, Calendar,
  Users, Search, Shield, PhoneCall, Eye, IndianRupee, Clock, XCircle
} from 'lucide-react';

import CustomerListDialog from '@/components/closure/CustomerListDialog';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const normalizePhone = (phone) => {
  if (!phone) return '';
  let p = String(phone).trim().replace(/[+\s-]/g, '');
  if (p.startsWith('91') && p.length > 10) p = p.slice(2);
  if (p.startsWith('0') && p.length > 10) p = p.slice(1);
  return p.length >= 10 ? p.slice(-10) : p;
};

const formatRevenue = (amount) => {
  if (!amount || amount <= 0) return '\u2014';
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return amount.toLocaleString('en-IN');
};

const formatRevenueFull = (amount) => {
  if (!amount || amount <= 0) return '\u20B90';
  return '\u20B9' + amount.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const getIvrStatus = (callCount) => {
  if (callCount >= 5) return 'green';
  if (callCount >= 1) return 'amber';
  return 'red';
};

const getCombinedStatus = (tlStatus, mgrStatus, hasManualVerification) => {
  if (hasManualVerification) return 'verified_manual';
  if (tlStatus === 'green' && mgrStatus === 'green') return 'green';
  if (tlStatus === 'red' && mgrStatus === 'red') return 'red';
  if ((tlStatus === 'red' && mgrStatus === 'amber') || (tlStatus === 'amber' && mgrStatus === 'red')) return 'red';
  return 'amber';
};

// ─── STATUS COMPONENTS ───────────────────────────────────────────────────────

const StatusBadge = ({ status, manualVerification }) => {
  if (status === 'verified' || status === 'verified_manual') {
    const statusLabels = {
      sale_happening: 'Sale Confirmed',
      sale_not_happening: 'No Sale',
      partial_delayed: 'Partial',
      followup_needed: 'Follow-up',
    };
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-blue-900/60 text-blue-300 border border-blue-700"
        title={manualVerification?.comment}
      >
        <CheckCircle className="w-3 h-3" />
        {manualVerification ? (statusLabels[manualVerification.sale_status] || 'Verified') : 'Verified'}
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded bg-cyan-900/60 text-cyan-300 border border-cyan-700">
        <Clock className="w-3 h-3" />
        In Progress
      </span>
    );
  }
  const configs = {
    green: { dot: 'bg-emerald-400', text: 'text-emerald-300', bg: 'bg-emerald-900/60', border: 'border-emerald-700', label: 'Active' },
    amber: { dot: 'bg-amber-400', text: 'text-amber-300', bg: 'bg-amber-900/60', border: 'border-amber-700', label: 'Partial' },
    red: { dot: 'bg-red-400', text: 'text-red-300', bg: 'bg-red-900/60', border: 'border-red-700', label: 'No Calls' },
  };
  const c = configs[status] || configs.red;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded ${c.bg} ${c.text} border ${c.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ${status === 'red' ? 'animate-pulse' : ''}`} />
      {c.label}
    </span>
  );
};

/** Phone icon + call count — shows in TL / Manager cells */
const CallStatus = ({ status, callCount }) => {
  const colors = {
    green: { icon: 'text-emerald-400', text: 'text-emerald-400' },
    amber: { icon: 'text-amber-400', text: 'text-amber-400' },
    red: { icon: 'text-red-400/60', text: 'text-red-500/60' },
  };
  const c = colors[status] || colors.red;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${c.text}`}>
      <PhoneCall className={`w-3 h-3 ${c.icon}`} />
      <span className="tabular-nums">{callCount}</span>
      <span className="text-slate-600 font-normal">{callCount === 1 ? 'call' : 'calls'}</span>
    </span>
  );
};

/** Three-color verification badge strip for a gym row */
const VerificationBadgeStrip = ({ summary }) => {
  if (!summary || !summary.total_leads) {
    return <span className="text-[11px] text-slate-600 italic">Not Verified</span>;
  }
  const { verified = 0, attempted = 0, negative = 0, total_leads = 0, unverified = 0 } = summary;
  const totalVerified = verified + attempted + negative;
  // If no verifications at all, show pending count
  if (totalVerified === 0) {
    return <span className="text-[11px] text-slate-600">{total_leads} pending</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {verified > 0 && (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" />
          {verified}/{total_leads}
        </span>
      )}
      {attempted > 0 && (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
          <Clock className="w-3 h-3" />
          {attempted}
        </span>
      )}
      {negative > 0 && (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          {negative}
        </span>
      )}
      {unverified > 0 && (
        <span className="text-[10px] text-slate-500 ml-0.5">
          {unverified} pending
        </span>
      )}
    </span>
  );
};

// ─── KPI CARD ────────────────────────────────────────────────────────────────

const ACCENTS = {
  slate:   { border: 'from-slate-500 to-slate-400',   iconBg: 'bg-slate-500/10',   iconColor: 'text-slate-300',   valueColor: 'text-white',       progressBg: 'bg-slate-500' },
  cyan:    { border: 'from-cyan-500 to-cyan-400',     iconBg: 'bg-cyan-500/10',     iconColor: 'text-cyan-400',    valueColor: 'text-cyan-400',    progressBg: 'bg-cyan-500' },
  amber:   { border: 'from-amber-500 to-amber-400',   iconBg: 'bg-amber-500/10',   iconColor: 'text-amber-400',   valueColor: 'text-amber-300',   progressBg: 'bg-amber-500' },
  emerald: { border: 'from-emerald-500 to-emerald-400', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400', valueColor: 'text-emerald-400', progressBg: 'bg-emerald-500' },
  purple:  { border: 'from-purple-500 to-purple-400', iconBg: 'bg-purple-500/10',   iconColor: 'text-purple-400',  valueColor: 'text-purple-400',  progressBg: 'bg-purple-500' },
  blue:    { border: 'from-blue-500 to-blue-400',     iconBg: 'bg-blue-500/10',     iconColor: 'text-blue-400',    valueColor: 'text-blue-300',    progressBg: 'bg-blue-500' },
};

const KpiCard = ({ icon: Icon, label, value, accent = 'slate', progress }) => {
  const a = ACCENTS[accent] || ACCENTS.slate;
  return (
    <div className="relative overflow-hidden rounded-lg bg-slate-800/60 border border-slate-700/50 transition-all duration-150 hover:scale-[1.02] hover:border-slate-600">
      {/* Colored top accent line */}
      <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${a.border}`} />
      <div className="px-4 py-3 flex items-center gap-3">
        <div className={`p-2 rounded-md ${a.iconBg} flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${a.iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium truncate">{label}</p>
          <p className={`text-lg font-bold tabular-nums ${a.valueColor}`}>{value}</p>
        </div>
      </div>
      {progress !== undefined && (
        <div className="px-4 pb-2.5 -mt-1">
          <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${a.progressBg} rounded-full transition-all duration-700 ease-out`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const ClosureExpected = () => {
  const navigate = useNavigate();

  // ── State ──
  const [closureData, setClosureData] = useState([]);
  const [ivrCallLogs, setIvrCallLogs] = useState([]);
  const [ivrC2cLogs, setIvrC2cLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    return ist.toISOString().split('T')[0];
  });
  const [gymFilter, setGymFilter] = useState('');
  const [tlFilter, setTlFilter] = useState('');
  const [mgrFilter, setMgrFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [selectedGymForCustomers, setSelectedGymForCustomers] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  // ── Data Fetching with AbortController ──
  const abortRef = useRef(null);
  const syncPollRef = useRef(null);

  const fetchData = useCallback(async (showLoading = true) => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (showLoading) setLoading(true);

    // Wrap an IVR request so it never hangs: resolve to empty fallback after `ms` ms.
    const ivrWithTimeout = (url, ms = 8000) => {
      const timeout = new Promise((resolve) =>
        setTimeout(() => resolve({ data: { data: [] } }), ms)
      );
      return Promise.race([
        axiosInstance.get(url, { signal: controller.signal }).catch(() => ({ data: { data: [] } })),
        timeout,
      ]);
    };

    try {
      const [closureRes, callRes, c2cRes] = await Promise.all([
        axiosInstance.get(`/closure-expected?date=${selectedDate}`, { signal: controller.signal }),
        ivrWithTimeout('/ivr/call_logs'),
        ivrWithTimeout('/ivr/c2c_logs'),
      ]);
      // Only update state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setClosureData(closureRes.data?.data || []);
        setSyncedAt(closureRes.data?.synced_at || null);
        setIvrCallLogs(callRes.data?.data || []);
        setIvrC2cLogs(c2cRes.data?.data || []);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return; // aborted, ignore
      toast.error('Failed to load closure data');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [selectedDate]);

  // Fetch on mount and when date changes
  useEffect(() => { fetchData(); }, [fetchData]);

  // Cleanup abort on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (syncPollRef.current) clearInterval(syncPollRef.current);
    };
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axiosInstance.post('/closure-expected/sync');
      const prevSyncedAt = res.data?.current_cache?.synced_at;
      toast.success('Sync started — will auto-refresh when complete (2-5 min)');

      // Poll for sync completion every 15 seconds
      if (syncPollRef.current) clearInterval(syncPollRef.current);
      let pollCount = 0;
      syncPollRef.current = setInterval(async () => {
        pollCount++;
        if (pollCount > 24) { // 6 min max
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncing(false);
          toast.info('Sync is taking longer than expected. Refresh manually.');
          return;
        }
        try {
          const statusRes = await axiosInstance.get('/closure-expected/sync-status');
          const newSyncedAt = statusRes.data?.synced_at;
          if (newSyncedAt && newSyncedAt !== prevSyncedAt) {
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            setSyncing(false);
            toast.success(`Sync complete — ${statusRes.data.total_leads} leads across ${statusRes.data.total_gyms} gyms`);
            fetchData();
          }
        } catch {
          // ignore poll errors
        }
      }, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
      setSyncing(false);
    }
  };

  // ── IVR call count lookup ──
  const callCountByPhone = useMemo(() => {
    const lookup = {};
    const allLogs = [
      ...ivrCallLogs.map(l => ({ phone: normalizePhone(l.attended_by), duration: parseFloat(l.call_duration) || 0, time: l.call_time || '' })),
      ...ivrC2cLogs.map(l => ({ phone: normalizePhone(l.agent_no), duration: parseFloat(l.call_duration) || 0, time: l.call_time || '' })),
    ];
    allLogs.forEach(({ phone, duration, time }) => {
      if (!phone || !time.startsWith(selectedDate)) return;
      if (!lookup[phone]) lookup[phone] = { totalCalls: 0, connectedCalls: 0, totalDuration: 0 };
      lookup[phone].totalCalls++;
      if (duration >= 120) {
        lookup[phone].connectedCalls++;
        lookup[phone].totalDuration += duration;
      }
    });
    return lookup;
  }, [ivrCallLogs, ivrC2cLogs, selectedDate]);

  // ── Enrich with IVR data ──
  const enrichedData = useMemo(() => {
    return closureData.map(gym => {
      const tlPhone = normalizePhone(gym.tl_phone || '');
      const mgrPhone = normalizePhone(gym.manager_phone || '');
      const tlCalls = callCountByPhone[tlPhone]?.connectedCalls || 0;
      const mgrCalls = callCountByPhone[mgrPhone]?.connectedCalls || 0;
      const tlStatus = getIvrStatus(tlCalls);
      const mgrStatus = getIvrStatus(mgrCalls);
      const verifications = gym.manual_verifications || [];
      const leadVerifications = gym.lead_verifications || [];
      const leadVerifiedCount = gym.lead_verified_count || 0;
      const leadConfirmedCount = gym.lead_confirmed_count || 0;
      const vs = gym.verification_summary || null;
      const hasManualVerification = verifications.length > 0 || leadConfirmedCount > 0;
      // Backend returns sorted by created_at ascending, so last = latest
      const latestVerification = hasManualVerification
        ? (verifications.length > 0 ? verifications[verifications.length - 1]
           : leadVerifications.length > 0 ? leadVerifications[leadVerifications.length - 1] : null)
        : null;

      // Determine combined_status using verification_summary when available
      let combined_status;
      if (vs && vs.is_fully_verified) {
        combined_status = 'verified';
      } else if (vs && vs.is_in_progress) {
        combined_status = 'in_progress';
      } else if (!vs) {
        // Backward compat: no verification_summary, use legacy IVR-based logic
        combined_status = getCombinedStatus(tlStatus, mgrStatus, hasManualVerification);
      } else {
        // verification_summary exists but no verifications yet — use IVR status
        combined_status = getCombinedStatus(tlStatus, mgrStatus, false);
      }

      return {
        ...gym,
        tl_call_count: tlCalls,
        tl_status: tlStatus,
        mgr_call_count: mgrCalls,
        mgr_status: mgrStatus,
        has_manual_verification: hasManualVerification,
        latest_verification: latestVerification,
        lead_verified_count: leadVerifiedCount,
        verification_summary: vs,
        combined_status,
      };
    });
  }, [closureData, callCountByPhone]);

  // ── Sort & Filter ──
  const statusOrder = { red: 0, amber: 1, in_progress: 2, verified_manual: 3, green: 3, verified: 4 };
  const sortedAndFiltered = useMemo(() => {
    let data = [...enrichedData];
    if (gymFilter) data = data.filter(g => g.gym_name?.toLowerCase().includes(gymFilter.toLowerCase()));
    if (tlFilter) data = data.filter(g => g.tl_name?.toLowerCase().includes(tlFilter.toLowerCase()));
    if (mgrFilter) data = data.filter(g => g.manager_name?.toLowerCase().includes(mgrFilter.toLowerCase()));
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'verified') {
        data = data.filter(g => g.combined_status === 'verified' || g.combined_status === 'verified_manual');
      } else if (statusFilter === 'in_progress') {
        data = data.filter(g => g.combined_status === 'in_progress');
      } else {
        data = data.filter(g => g.combined_status === statusFilter);
      }
    }
    return data.sort((a, b) => {
      const s = (statusOrder[a.combined_status] ?? 1) - (statusOrder[b.combined_status] ?? 1);
      return s !== 0 ? s : b.total_closure_expected - a.total_closure_expected;
    });
  }, [enrichedData, gymFilter, tlFilter, mgrFilter, statusFilter]);

  // ── Summary KPIs ──
  const summary = useMemo(() => {
    const total = enrichedData.length;
    const tlCalling = enrichedData.filter(g => g.tl_status !== 'red').length;
    const mgrCalling = enrichedData.filter(g => g.mgr_status !== 'red').length;
    const manuallyVerified = enrichedData.filter(g => g.has_manual_verification).length;
    return {
      totalGyms: total,
      totalClosures: enrichedData.reduce((s, g) => s + (g.total_closure_expected || 0), 0),
      totalTlCommitted: enrichedData.reduce((s, g) => s + (g.tl_committed || 0), 0),
      totalRevenue: enrichedData.reduce((s, g) => s + (g.total_revenue || 0), 0),
      pctTlCalling: total ? Math.round((tlCalling / total) * 100) : 0,
      pctMgrCalling: total ? Math.round((mgrCalling / total) * 100) : 0,
      manuallyVerified,
    };
  }, [enrichedData]);

  // ── Synced-at text ──  (recalculates when syncedAt or loading changes)
  const syncedAgoText = useMemo(() => {
    if (!syncedAt) return null;
    const diff = Math.round((Date.now() - new Date(syncedAt).getTime()) / 60000);
    if (diff < 1) return 'just now';
    if (diff === 1) return '1 min ago';
    if (diff < 60) return `${diff} min ago`;
    return `${Math.floor(diff / 60)}h ago`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedAt, loading]); // loading triggers recalculation on refresh

  // ── Handlers ──
  const handleViewLeads = (gym) => { setSelectedGymForCustomers(gym); setCustomerDialogOpen(true); };
  const handleGymClick = () => { navigate(`/commitment-dashboard?date=${selectedDate}`); };

  const today = useMemo(() => {
    const now = new Date();
    const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    return ist.toISOString().split('T')[0];
  }, []);

  // ─── RENDER ────────────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div className="bg-slate-900 border-b border-slate-700/50 px-4 py-3 sticky top-0 z-30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
            <Target className="w-5 h-5 text-cyan-400" />
            Closure Verification
          </h1>
          {syncedAgoText && (
            <span className="text-[10px] text-slate-500 flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-40" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Synced {syncedAgoText}
            </span>
          )}
          {!loading && enrichedData.length > 0 && (
            <span className="text-[10px] text-slate-500 tabular-nums">{enrichedData.length} gyms</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={today}
            className="px-2.5 py-1 rounded-md border border-slate-700 bg-slate-800/80 text-slate-200 text-xs focus:border-cyan-600 focus:ring-1 focus:ring-cyan-600/30 outline-none"
          />
          <button
            onClick={() => fetchData()}
            className="p-1.5 rounded-md border border-slate-700 bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {isAdmin && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-cyan-700/50 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        {renderHeader()}
        <div className="flex items-center justify-center h-80">
          <div className="text-center space-y-3">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto text-slate-500" />
            <p className="text-sm text-slate-500">Loading closure data...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty ──
  if (enrichedData.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        {renderHeader()}
        <div className="flex items-center justify-center h-80">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-xl bg-slate-800/60 border border-slate-700/40 flex items-center justify-center">
              <Calendar className="w-7 h-7 text-slate-700" />
            </div>
            <div>
              <p className="text-slate-300 font-medium text-sm">No closures expected</p>
              <p className="text-[11px] text-slate-600 mt-1">Check WTF API sync or select a different date</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Status pill counts ──
  const statusCounts = {
    all: enrichedData.length,
    red: enrichedData.filter(g => g.combined_status === 'red').length,
    amber: enrichedData.filter(g => g.combined_status === 'amber').length,
    green: enrichedData.filter(g => g.combined_status === 'green').length,
    in_progress: enrichedData.filter(g => g.combined_status === 'in_progress').length,
    verified: enrichedData.filter(g => g.combined_status === 'verified' || g.combined_status === 'verified_manual').length,
  };

  const getRowBorder = (status) => {
    if (status === 'verified' || status === 'verified_manual') return 'border-l-4 border-l-blue-500';
    if (status === 'in_progress') return 'border-l-4 border-l-cyan-500';
    if (status === 'green') return 'border-l-4 border-l-emerald-500';
    if (status === 'amber') return 'border-l-4 border-l-amber-500';
    if (status === 'red') return 'border-l-4 border-l-red-500 bg-red-950/20';
    return 'border-l-4 border-l-slate-700';
  };

  const STATUS_PILLS = [
    { key: 'all',         label: 'All',         active: 'text-white bg-slate-600/80 border-slate-500 shadow-[0_0_10px_rgba(148,163,184,0.1)]' },
    { key: 'red',         label: 'No Calls',    active: 'text-red-300 bg-red-500/15 border-red-500/40 shadow-[0_0_10px_rgba(239,68,68,0.12)]' },
    { key: 'amber',       label: 'Minimal',     active: 'text-amber-300 bg-amber-500/15 border-amber-500/40 shadow-[0_0_10px_rgba(245,158,11,0.12)]' },
    { key: 'green',       label: 'Active',      active: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40 shadow-[0_0_10px_rgba(52,211,153,0.12)]' },
    { key: 'in_progress', label: 'In Progress', active: 'text-cyan-300 bg-cyan-500/15 border-cyan-500/40 shadow-[0_0_10px_rgba(34,211,238,0.12)]' },
    { key: 'verified',    label: 'Verified',    active: 'text-blue-300 bg-blue-500/15 border-blue-500/40 shadow-[0_0_10px_rgba(59,130,246,0.12)]' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {renderHeader()}

      <div className="px-4 py-4 space-y-3">

      {/* ─── KPI CARDS ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <KpiCard icon={Users}       label="Gyms"           value={summary.totalGyms}                              accent="slate" />
        <KpiCard icon={Target}      label="WTF Leads"      value={summary.totalClosures}                          accent="cyan" />
        <KpiCard icon={IndianRupee} label="Expected Value"  value={formatRevenueFull(summary.totalRevenue)}       accent="emerald" />
        <KpiCard icon={CheckCircle} label="TL Committed"   value={summary.totalTlCommitted || '\u2014'}           accent="amber" />
        <KpiCard icon={Phone}       label="TL Calling"     value={`${summary.pctTlCalling}%`}                     accent="emerald" progress={summary.pctTlCalling} />
        <KpiCard icon={Phone}       label="Mgr Calling"    value={`${summary.pctMgrCalling}%`}                    accent="purple"  progress={summary.pctMgrCalling} />
        <KpiCard icon={Shield}      label="Verified"       value={summary.manuallyVerified}                       accent="blue" />
      </div>

      {/* ─── FILTERS ────────────────────────────────────────────────────── */}
      <div className="bg-slate-900/60 border border-slate-700/40 rounded-lg p-3 space-y-2.5">
        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_PILLS.map(({ key, label, active }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1 rounded-md text-[11px] font-semibold border transition-all duration-150 ${
                statusFilter === key
                  ? active
                  : 'text-slate-500 bg-slate-800/40 border-slate-700/50 hover:text-slate-300 hover:border-slate-600'
              }`}
            >
              {label}
              <span className="ml-1.5 tabular-nums opacity-60">{statusCounts[key]}</span>
            </button>
          ))}
        </div>
        {/* Search inputs */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" />
            <input
              type="text"
              placeholder="Search gym..."
              value={gymFilter}
              onChange={(e) => setGymFilter(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 rounded-md border border-slate-700/40 bg-slate-800/40 text-slate-200 text-xs placeholder:text-slate-600 focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/15 outline-none transition-colors"
            />
          </div>
          <input
            type="text"
            placeholder="TL name..."
            value={tlFilter}
            onChange={(e) => setTlFilter(e.target.value)}
            className="px-2.5 py-1.5 rounded-md border border-slate-700/40 bg-slate-800/40 text-slate-200 text-xs w-28 placeholder:text-slate-600 focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/15 outline-none transition-colors"
          />
          <input
            type="text"
            placeholder="Manager..."
            value={mgrFilter}
            onChange={(e) => setMgrFilter(e.target.value)}
            className="px-2.5 py-1.5 rounded-md border border-slate-700/40 bg-slate-800/40 text-slate-200 text-xs w-28 placeholder:text-slate-600 focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/15 outline-none transition-colors"
          />
          <span className="text-[10px] text-slate-600 ml-auto tabular-nums">
            {sortedAndFiltered.length}/{enrichedData.length}
          </span>
        </div>
      </div>

      {/* ─── DATA TABLE ─────────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-lg border border-slate-700/40 bg-slate-900/50">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-800/90 border-b border-slate-700/60">
              <th className="text-left  px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Gym</th>
              <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Leads</th>
              <th className="text-right  px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Value</th>
              <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">TL Commit</th>
              <th className="text-left  px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Team Leader</th>
              <th className="text-left  px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Manager</th>
              <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Verification</th>
              <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Status</th>
              <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFiltered.map((gym, idx) => (
              <tr
                key={gym.gym_id || idx}
                className={`border-b border-slate-700/40 transition-colors hover:bg-slate-800/40 ${getRowBorder(gym.combined_status)}`}
              >
                {/* Gym Name */}
                <td className="px-3 py-2.5 min-w-[180px]">
                  <button
                    onClick={() => handleGymClick(gym)}
                    className="font-medium text-slate-200 hover:text-white text-left text-sm transition-colors"
                  >
                    {gym.gym_name || 'Unknown Gym'}
                  </button>
                  {gym.unmapped && (
                    <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-bold uppercase tracking-wider">
                      Unmapped
                    </span>
                  )}
                </td>

                {/* WTF Leads */}
                <td className="text-center px-3 py-2.5">
                  <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-bold text-sm tabular-nums">
                    {gym.total_closure_expected || 0}
                  </span>
                </td>

                {/* Revenue / Value */}
                <td className="text-right px-3 py-2.5">
                  <span className={`text-xs font-semibold tabular-nums ${gym.total_revenue > 0 ? 'text-emerald-400' : 'text-slate-700'}`}>
                    {gym.total_revenue > 0 ? formatRevenue(gym.total_revenue) : '\u2014'}
                  </span>
                </td>

                {/* TL Committed */}
                <td className="text-center px-3 py-2.5">
                  {gym.tl_committed ? (
                    <span className={`text-sm font-semibold tabular-nums ${
                      gym.tl_committed > (gym.total_closure_expected || 0) ? 'text-amber-400' : 'text-slate-300'
                    }`}>
                      {gym.tl_committed}
                    </span>
                  ) : (
                    <span className="text-slate-700">{'\u2014'}</span>
                  )}
                </td>

                {/* Team Leader + Call Status */}
                <td className="px-3 py-2.5">
                  <div className="text-sm font-medium text-slate-200 leading-tight">
                    {gym.tl_name || <span className="italic text-slate-600 text-xs">Unassigned</span>}
                  </div>
                  <div className="mt-0.5">
                    <CallStatus status={gym.tl_status} callCount={gym.tl_call_count} />
                  </div>
                </td>

                {/* Manager + Call Status */}
                <td className="px-3 py-2.5">
                  <div className="text-sm font-medium text-slate-200 leading-tight">
                    {gym.manager_name || <span className="italic text-slate-600 text-xs">Unassigned</span>}
                  </div>
                  <div className="mt-0.5">
                    <CallStatus status={gym.mgr_status} callCount={gym.mgr_call_count} />
                  </div>
                </td>

                {/* Verification Badge Strip */}
                <td className="text-center px-3 py-2.5">
                  <VerificationBadgeStrip summary={gym.verification_summary} />
                </td>

                {/* Combined Status */}
                <td className="text-center px-3 py-2.5">
                  <StatusBadge status={gym.combined_status} manualVerification={gym.latest_verification} />
                </td>

                {/* Action Buttons */}
                <td className="text-center px-3 py-2.5">
                  <button
                    onClick={() => handleViewLeads(gym)}
                    disabled={gym.unmapped}
                    className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed mx-auto"
                  >
                    <Eye className="w-3 h-3" />
                    Leads
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </div>{/* end content wrapper */}

      {/* ─── DIALOGS ────────────────────────────────────────────────────── */}
      <CustomerListDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        gym={selectedGymForCustomers}
        date={selectedDate}
      />
    </div>
  );
};

export default ClosureExpected;

